import React, { useEffect, useRef, useState } from "react";
import { Dialog, useMediaQuery } from "@material-ui/core";
import { useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import styles from "./CardPaymentBanner.module.scss";

const DEMO_BASE = `${process.env.PUBLIC_URL || ""}/card-pay/v1`;
const DEMO_URL = `${DEMO_BASE}/index.html`;
const CONTACT_URL = "mailto:office@simons.rs?subject=Upit%20za%20CARD%20PAY";
const Arrow = () => <span aria-hidden="true">↗</span>;

const CardPaymentBanner = () => {
  const user = useSelector((state) => state.auth.user);
  const enabled = user?.banners?.cardPayment ?? user?.showAd;
  const [isOpen, setIsOpen] = useState(false);
  const frameRef = useRef(null);
  const viewed = useRef(false);
  const posthog = usePostHog();
  const mobile = useMediaQuery("(max-width:640px)");
  const reducedMotion = useMediaQuery("(prefers-reduced-motion:reduce)");
  const track = (event, properties = {}) => posthog?.capture(event, {
    campaign: "card_pay_3d_v1", page_location: window.location.pathname, ...properties,
  });

  useEffect(() => {
    if (enabled && !viewed.current) {
      viewed.current = true;
      posthog?.capture("card_payment_banner_viewed", { campaign: "card_pay_3d_v1" });
    }
  }, [enabled, posthog]);

  useEffect(() => {
    if (!isOpen) return;
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow) return;
      if (event.data?.type === "cardpay:close") {
        setIsOpen(false);
      } else if (event.data?.type === "cardpay:demo" &&
        ["started", "completed"].includes(event.data.action) &&
        Number.isInteger(event.data.quantity) && event.data.quantity >= 1 && event.data.quantity <= 8) {
        posthog?.capture(`card_payment_demo_${event.data.action}`, {
          campaign: "card_pay_3d_v1", quantity: event.data.quantity,
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [isOpen, posthog]);

  useEffect(() => {
    if (!isOpen || !enabled) return;
    const openedAt = Date.now();
    posthog?.capture("card_payment_modal_opened", { campaign: "card_pay_3d_v1" });
    return () => posthog?.capture("card_payment_modal_closed", {
      campaign: "card_pay_3d_v1",
      modal_open_duration_seconds: Math.round((Date.now() - openedAt) / 1000),
    });
  }, [isOpen, enabled, posthog]);

  const openDemo = () => {
    track("card_payment_banner_clicked");
    setIsOpen(true);
  };
  if (!enabled) return null;

  return (
    <>
      <section className={styles.banner} aria-labelledby="card-pay-banner-title">
        <div className={styles.copy}>
          <p className={styles.eyebrow}><span /> SIMONS / CARD PAY</p>
          <h3 id="card-pay-banner-title" className={styles.title}>
            Uvedite kartično plaćanje<br className={styles.desktopBreak} /> u Vašu perionicu.
          </h3>
          <p className={styles.description}>Gost prisloni karticu. Preuzme žetone. Spreman je za pranje.</p>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={openDemo}>
              Isprobajte 3D demo <Arrow />
            </button>
            <a className={styles.contactLink} href={CONTACT_URL}
              onClick={() => track("card_payment_contact_clicked", { location: "banner" })}>
              Zatražite ponudu
            </a>
          </div>
        </div>
        <button type="button" className={styles.preview} onClick={openDemo}
          aria-label="Otvorite interaktivni CARD PAY prikaz">
          <span className={styles.previewLabel}>UPOZNAJTE UREĐAJ</span>
          <img src={`${DEMO_BASE}/assets/card-pay-poster.png`} alt="SIMONS CARD PAY uređaj"
            width="544" height="440" decoding="async" />
          <span className={styles.previewHint}>Rotirajte. Isprobajte. <Arrow /></span>
        </button>
      </section>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullScreen={mobile}
        maxWidth={false} classes={{ paper: styles.dialog }}
        aria-labelledby="card-pay-dialog-title" transitionDuration={reducedMotion ? 0 : 180}>
        <header className={styles.dialogHeader}>
          <div>
            <p className={styles.dialogEyebrow}>SIMONS / CARD PAY</p>
            <h2 id="card-pay-dialog-title">Isprobajte uređaj.</h2>
          </div>
          <button type="button" className={styles.close} onClick={() => setIsOpen(false)}
            aria-label="Zatvorite CARD PAY prikaz" autoFocus>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </header>
        {isOpen && <iframe ref={frameRef} className={styles.frame} src={`${DEMO_URL}?embed`}
          title="Interaktivna CARD PAY demonstracija bez stvarne naplate" />}
        <footer className={styles.dialogFooter}>
          <a className={styles.fullView} href={DEMO_URL} target="_blank" rel="noopener noreferrer"
            onClick={() => track("card_payment_full_view_clicked")}>
            Otvori ceo prikaz <Arrow />
          </a>
          <a className={styles.primary} href={CONTACT_URL}
            onClick={() => track("card_payment_contact_clicked", { location: "demo" })}>
            Zatražite ponudu <Arrow />
          </a>
        </footer>
      </Dialog>
    </>
  );
};
export default CardPaymentBanner;
